import React, { useCallback, useContext, useEffect, useState } from "react";
import "src/components/contact/Contact.css";
import { ContactItem, ContactItemRow } from "src/components/contact/contactitem/ContactItem";
import { useQuery } from "@apollo/client";
import {
    GetContactInformationByRealestateId,
    GetContactInformationByRealestateIdVariables
} from "src/api/generated/GetContactInformationByRealestateId";
import { GET_CONTACT_INFORMATION_BY_REALESTATEID } from "src/api/contact";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { submitUserFeedback } from "src/utils/Feedback";
import { RootStoreContext } from "src/stores/RootStore";
import { ErpType, Role } from "src/network/User";
import { NetworkConfig } from "src/network/NetworkConfig";
import { ColorStyle } from "src/utils/Colors";

export enum ContactItemType {
    PHONE,
    MAIL,
    DEVICE
}

export enum Group {
    REALESTATE,
    FACILITYMANAGEMENT
}

interface ITenantItem {
    tenantid: string;
    tenantname: string;
}

interface ContactProps {
    realestateid: string;
}

export const Contact = (props: ContactProps) => {
    const { t } = useTranslation();
    const { navStore, authStore } = useContext(RootStoreContext);

    const [showModal, setShowModal] = useState<boolean>(false);
    const [easyContactUrl, setEasyContactUrl] = useState<string>("");
    const [allSameTenantId, setAllSameTenantId] = useState<boolean>(false);
    const [tenantList, setTenantList] = useState<ITenantItem[]>([]);

    const handleSubmitFeedback = useCallback(() => {
        submitUserFeedback(navStore.customer);
    }, [navStore.customer]);

    const { loading, error, data } = useQuery<
        GetContactInformationByRealestateId,
        GetContactInformationByRealestateIdVariables
    >(GET_CONTACT_INFORMATION_BY_REALESTATEID, {
        variables: { realestateid: props.realestateid, consolidationid: props.realestateid }
    });

    // Helper function to set tenant list
    const setTenantListFromData = (source: any[]) => {
        setTenantList(
            source.map((tenant) => ({
                tenantid: tenant.tenantid || "",
                tenantname: tenant.tenantname || ""
            }))
        );
    };

    // Helper function to generate EasyContact URL
    const generateEasyContactUrl = (prefix: string, id: string | undefined) => {
        if (!id) return;
        const token = btoa(`${prefix}=${id}`);
        setEasyContactUrl(`${NetworkConfig.proxy}/ec/start?t=${token}`);
    };

    useEffect(() => {
        if (!data) return;

        // Set tenantList based on available data
        const coownerConsolidation = data.coownertentantbyconsolidation;
        const tenantConsolidation = data.tentantbyconsolidation;

        if (coownerConsolidation?.length) {
            setTenantListFromData(coownerConsolidation);
        } else if (tenantConsolidation?.length) {
            setTenantListFromData(tenantConsolidation);
        }

        // Handle EasyContactUrl setup based on user role
        if (navStore.hasFeatureEasyContact && authStore.user?.role) {
            const userRole = authStore.user.role;

            // Manager/Owner handling
            if (userRole === Role.MANAGER || userRole === Role.OWNER) {
                generateEasyContactUrl("H", data.house?.[0]?.id);
            }

            // Coowner handling
            if (userRole === Role.COOWNER) {
                const tenantId = data.coownertentant?.[0]?.tenantid;
                generateEasyContactUrl("T", tenantId);

                if (coownerConsolidation?.length > 1) {
                    const tenantIds = coownerConsolidation.map((item) => item.tenantid);
                    const allSame = tenantIds.every((id) => id === tenantIds[0]);
                    setAllSameTenantId(allSame);
                }

                if (coownerConsolidation?.length === 1 || allSameTenantId) {
                    generateEasyContactUrl("T", coownerConsolidation?.[0]?.tenantid);
                }
            }

            // Tenant handling
            if (userRole === Role.TENANT) {
                const tenantId = data.tentant?.[0]?.tenantid;
                generateEasyContactUrl("T", tenantId);

                if (tenantConsolidation?.length > 1) {
                    const tenantIds = tenantConsolidation.map((item) => item.tenantid);
                    const allSame = tenantIds.every((id) => id === tenantIds[0]);
                    setAllSameTenantId(allSame);
                }

                if (tenantConsolidation?.length === 1 || allSameTenantId) {
                    generateEasyContactUrl("T", tenantConsolidation?.[0]?.tenantid);
                }
            }

            // Fallback EasyContact URL
            if (!easyContactUrl) {
                setEasyContactUrl(`${NetworkConfig.proxy}/ec`);
            }
        }
    }, [data, authStore.user, navStore.hasFeatureEasyContact, easyContactUrl, allSameTenantId]);

    const onClickContactButton = useCallback(() => {
        setShowModal(true);
    }, []);

    const toggleModal = useCallback(() => {
        setShowModal(false);
    }, []);

    if (loading) {
        return <UI.LoadingCard />;
    }
    if (!(data && data.common_realestatecontacts) || error) {
        return <UI.ErrorCard onSubmitFeedback={handleSubmitFeedback} message={t("error.nodata").toString()} />;
    }
    if (!(data.common_realestatecontacts.length > 0)) {
        return <UI.Card>{t("error.emptydata")}</UI.Card>;
    }

    const portalContact = data.common_realestatecontacts[0];

    const contentRealestate: Array<ContactItemRow> = [
        { type: ContactItemType.PHONE, content: portalContact.managerphone },
        { type: ContactItemType.DEVICE, content: portalContact.managermobile },
        { type: ContactItemType.MAIL, content: portalContact.manageremail }
    ];

    const contentAccountant: Array<{ type: ContactItemType; content: string | null }> = [
        { type: ContactItemType.PHONE, content: portalContact.accountantphone },
        { type: ContactItemType.DEVICE, content: portalContact.accountantmobile },
        { type: ContactItemType.MAIL, content: portalContact.accountantemail }
    ];

    let contentFacilityManagemant: Array<{ type: ContactItemType; content: string | null }> = [
        { type: ContactItemType.PHONE, content: portalContact.facilityphone },
        { type: ContactItemType.DEVICE, content: portalContact.facilitymobile },
        { type: ContactItemType.MAIL, content: portalContact.facilityemail }
    ];

    const portalContactChildren = data.common_realestatecontacts_children[0];
    if (portalContactChildren && authStore.user?.erpType === ErpType.IT2) {
        contentFacilityManagemant = [
            { type: ContactItemType.PHONE, content: portalContactChildren.facilityphone },
            { type: ContactItemType.DEVICE, content: portalContactChildren.facilitymobile },
            { type: ContactItemType.MAIL, content: portalContactChildren.facilityemail }
        ];
    }

    return (
        <UI.Row className="ContactTile d-flex flex-column flex-md-row flex-xl-column">
            {easyContactUrl.length > 0 ? (
                <UI.Col md={12}>
                    {tenantList.length > 1 && !allSameTenantId ? (
                        <UI.Button
                            className="ContactButton mb-3"
                            label="Online kontaktieren"
                            onClick={onClickContactButton}
                        />
                    ) : (
                        <a href={easyContactUrl} className="link d-flex" rel="noopener noreferrer" target="_blank">
                            <UI.Button className="ContactButton mb-3" label="Online kontaktieren" />
                        </a>
                    )}
                </UI.Col>
            ) : undefined}
            {showModal ? (
                <UI.Modal show={showModal} onClose={toggleModal} isScrollable={true}>
                    <div className="mb-3 ModalTitle">Auswahl</div>
                    {(() => {
                        const usedTenantIds = new Set();

                        return tenantList
                            .filter((tenant) => {
                                // Check if tenantid is already in the set
                                if (!usedTenantIds.has(tenant.tenantid)) {
                                    usedTenantIds.add(tenant.tenantid); // Mark tenantid as used
                                    return true;
                                }
                                return false; // Exclude duplicates
                            })
                            .map((tenant, index) => {
                                const tenantToken = btoa(`T=${tenant.tenantid}`);
                                return (
                                    <a
                                        key={index}
                                        href={`${NetworkConfig.proxy}/ec/start?t=${tenantToken}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="modal-link"
                                    >
                                        <div className="TenantName">{tenant.tenantname}</div>
                                        <UI.Icon icon={UI.SVGIcon.ChevronRight} color={ColorStyle("primary")} />
                                    </a>
                                );
                            });
                    })()}
                    <div className="text-center">
                        <UI.Button className={"mt-3"} label={t("labels.cancel")} onClick={toggleModal} variant="link" />
                    </div>
                </UI.Modal>
            ) : undefined}

            <>
                {portalContact.managername ? (
                    <UI.Col>
                        <div className="ContactRealestate">
                            <div className="ContactTitle">
                                {authStore.user?.role === Role.TENANT ? (
                                    <>
                                        {t("screens.contact.partner")} {t("screens.contact.management")}
                                    </>
                                ) : (
                                    <>
                                        {t("screens.contact.partner")} {t("screens.contact.management_accountant")}
                                    </>
                                )}
                            </div>
                            <ContactItem name={portalContact.managername} contacts={contentRealestate} />
                        </div>
                    </UI.Col>
                ) : undefined}
                {portalContact.facilityname || portalContactChildren?.facilityname ? (
                    <UI.Col>
                        <div className="ContactRealestate">
                            <div className="ContactTitle">
                                {t("screens.contact.partner")} {t("screens.contact.facility")}
                            </div>
                            <ContactItem
                                name={
                                    authStore.user?.erpType === ErpType.IT2 && portalContactChildren
                                        ? portalContactChildren.facilityname
                                        : portalContact.facilityname
                                }
                                contacts={contentFacilityManagemant}
                            />
                        </div>
                    </UI.Col>
                ) : undefined}
                {portalContact.accountantname ? (
                    <UI.Col>
                        <div>
                            <div className="ContactRealestate">
                                <div className="ContactTitle">
                                    {t("screens.contact.partner")} {t("screens.contact.accountant")}
                                </div>
                                <ContactItem name={portalContact.accountantname} contacts={contentAccountant} />
                            </div>
                        </div>
                    </UI.Col>
                ) : undefined}
            </>
        </UI.Row>
    );
};
